import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import close from '../../../../assets/icons/close.svg';
import Notifier from '../../../../Utils/notify';
import {
  revokeReconciliationReport,
  friendlyReconciliationError,
} from '../../../../API/reconciliationApi';
import { REPORT_TYPE_LABELS } from '../../../../Utils/permissionCodes';
import { formatDateDDMMYYYYHHMMSS_UTC } from '../../../../Utils/data&dateTime';
import '../../../../assets/css/Modal.css';

export default function RevokeReportModal({
  isOpen,
  onClose,
  report, // the report row to revoke; must include id, report_type, submitted_by, submitted_at
  studentName,
  onSuccess,
}) {
  const [pin, setPin] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const reasonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setPin('');
      setReason('');
      return;
    }
    const t = setTimeout(() => reasonRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [isOpen]);

  if (!isOpen || !report) return null;

  const handleRevoke = async () => {
    if (!reason.trim()) {
      Notifier.error('Please enter a reason for revoking');
      return;
    }
    if (pin.length !== 4) {
      Notifier.error('PIN must be 4 digits');
      return;
    }

    try {
      setSubmitting(true);
      const res = await revokeReconciliationReport({
        report_id: report.id,
        pin,
        reason: reason.trim(),
      });
      if (res?.error) {
        Notifier.error(friendlyReconciliationError(res.error));
        return;
      }
      Notifier.success('Reconciliation report revoked');
      onSuccess?.(res?.report);
      onClose();
    } catch (e) {
      console.error('revokeReconciliationReport failed', e);
      Notifier.error('Could not revoke report. Please retry.');
    } finally {
      setSubmitting(false);
    }
  };

  const reportLabel = REPORT_TYPE_LABELS[report.report_type] || report.report_type;

  return createPortal(
    <div className="modal-overlay" style={{ zIndex: 9999999 }}>
      <div
        className="modal-content"
        style={{
          position: 'relative',
          width: '460px',
          maxWidth: '92vw',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <div
          className="modal-header-content"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <h2 style={{ margin: 0, color: '#d4380d', fontSize: '18px' }}>
            Revoke {reportLabel} Reconciliation Report
          </h2>
          <button className="close-btn" onClick={onClose}>
            <img src={close} alt="close" />
          </button>
        </div>

        <div style={{ fontSize: 13, color: '#6B778C' }}>
          Student: <strong style={{ color: '#222F3E' }}>{studentName}</strong>
        </div>

        <div
          style={{
            background: '#FFF7F0',
            border: '1px dashed #f0a37a',
            borderRadius: 8,
            padding: '12px 14px',
            fontSize: 13,
          }}
        >
          <div>
            <span style={{ color: '#6B778C' }}>Submitted by: </span>
            <strong>{report.submitted_by || (report.is_backfilled ? 'System (deploy backfill)' : '—')}</strong>
          </div>
          <div>
            <span style={{ color: '#6B778C' }}>Submitted at: </span>
            <strong>{formatDateDDMMYYYYHHMMSS_UTC(report.submitted_at) || '—'}</strong>
          </div>
          <div style={{ marginTop: 8, color: '#d4380d' }}>
            Revoking removes only this verification record. It does <strong>not</strong>
            change the Library NOC, fee ledger, or precheck status. The student
            will be ineligible to receive the admit card until a new report is submitted.
          </div>
        </div>

        <div>
          <label className="form-labell">Reason (required)</label>
          <textarea
            ref={reasonRef}
            className="form-control"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why are you revoking this report?"
          />
        </div>

        <div>
          <label className="form-labell">Security PIN</label>
          <input
            type="password"
            inputMode="numeric"
            maxLength={4}
            className="form-control"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            placeholder="Enter your 4-digit PIN"
            autoComplete="off"
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            className="add-btn"
            style={{
              padding: '10px 16px',
              background: '#fff',
              color: '#222F3E',
              border: '1px solid #d3d3e8',
              borderRadius: 8,
              cursor: 'pointer',
            }}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="add-btn"
            style={{
              padding: '10px 16px',
              background: pin.length === 4 && reason.trim() && !submitting ? '#d4380d' : '#d3d3d3',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: pin.length === 4 && reason.trim() && !submitting ? 'pointer' : 'not-allowed',
              fontWeight: 600,
            }}
            disabled={pin.length !== 4 || !reason.trim() || submitting}
            onClick={handleRevoke}
          >
            {submitting ? 'Revoking…' : 'Revoke Report'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
